import { Plus, Trash2 } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { MetadataRunConditionDto } from '@/Domain/api/Shared/dto/MetadataRunCondition.dto';
import { ParameterDropdown } from '@/Domain/components/Action/Lexical/component/ParameterDropdown';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Field, FieldGroup, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { MetadataConditionConnector } from '@/Domain/components/Shared/MetadataCondition/MetadataConditionConnector';
import { MetadataConditionOperator } from '@/Domain/components/Shared/MetadataCondition/MetadataConditionOperator';
import { MetadataConditionValue } from '@/Domain/components/Shared/MetadataCondition/MetadataConditionValue';
import {
  ALERT_RUN_CONDITIONS_MAX_COUNT,
  alertRunConditionUsesValue,
} from '@/Domain/helper/alertRunCondition.helper';
import { changeMetadataConditionOperator } from '@/Domain/helper/metadataRunCondition.helper';

type Props = {
  conditions: MetadataRunConditionDto[];
  isEditing: boolean;
  onChange?: (conditions: MetadataRunConditionDto[]) => void;
  testIdPrefix: string;
};

export const AlertRunConditions = ({ conditions, isEditing, onChange, testIdPrefix }: Props) => {
  const { t: tCommon } = useTranslation('metadata_conditions');
  const { t } = useTranslation('insights_page', {
    keyPrefix: 'manage_alerts.form.run_conditions',
  });

  const updateCondition = (index: number, update: Partial<MetadataRunConditionDto>): void => {
    onChange?.(
      conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, ...update } : condition,
      ),
    );
  };

  const addCondition = (): void => {
    onChange?.([
      ...conditions,
      {
        id: uuidv4(),
        metadata_key: '',
        operator: 'equals',
        value: '',
        values: [],
      },
    ]);
  };

  const removeCondition = (index: number): void => {
    onChange?.(conditions.filter((_, conditionIndex) => conditionIndex !== index));
  };

  if (!isEditing) {
    if (conditions.length === 0) {
      return <p className="py-2 text-sm text-muted-foreground">{t('all_conversations')}</p>;
    }

    return (
      <div className="flex min-w-0 flex-col gap-2 py-1.5">
        {conditions.map((condition, index) => {
          const operatorLabel = tCommon(`operator.${condition.operator}`);
          const label =
            condition.operator === 'is_one_of'
              ? `${condition.metadata_key} ${operatorLabel} ${(condition.values ?? []).map((value) => JSON.stringify(value)).join(', ')}`
              : alertRunConditionUsesValue(condition.operator)
                ? `${condition.metadata_key} ${operatorLabel} ${condition.value}`
                : `${condition.metadata_key} ${operatorLabel}`;
          return (
            <Fragment key={condition.id}>
              {index > 0 && <MetadataConditionConnector />}
              <Badge
                variant="secondary"
                size="md"
                className="box-border h-auto max-w-full"
                title={label}
                data-testid={`${testIdPrefix}-condition-summary-${index}`}
              >
                <span className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {label}
                </span>
              </Badge>
            </Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {conditions.map((condition, index) => {
        const keyInputId = `${testIdPrefix}-condition-key-${index}`;
        const operatorInputId = `${testIdPrefix}-condition-operator-${index}`;
        const valueInputId = `${testIdPrefix}-condition-value-${index}`;

        return (
          <Fragment key={condition.id}>
            {index > 0 && <MetadataConditionConnector />}
            <FieldGroup className="gap-2 rounded-xl border border-border bg-muted p-2">
              <div className="flex items-start gap-1">
                <Field className="min-w-0 flex-1">
                  <FieldLabel htmlFor={keyInputId} className="sr-only">
                    {t('metadata_key')}
                  </FieldLabel>
                  <Input
                    id={keyInputId}
                    size="sm"
                    maxLength={100}
                    value={condition.metadata_key}
                    onChange={(event) =>
                      updateCondition(index, { metadata_key: event.target.value })
                    }
                    placeholder={t('metadata_key_placeholder')}
                    data-testid={keyInputId}
                  />
                </Field>
                <div data-testid={`${testIdPrefix}-condition-key-picker-${index}`}>
                  <ParameterDropdown
                    onlyMetadataKeys
                    parameters={[]}
                    onSelect={(option) =>
                      updateCondition(index, {
                        metadata_key: option.name.replace(/^metadata_/, ''),
                      })
                    }
                  />
                </div>
                <IconButton
                  size="sm"
                  variant="secondary"
                  onClick={() => removeCondition(index)}
                  aria-label={t('remove_aria', { index: index + 1 })}
                  data-testid={`${testIdPrefix}-condition-remove-${index}`}
                >
                  <Trash2 />
                </IconButton>
              </div>

              <Field className="min-w-0">
                <FieldLabel htmlFor={operatorInputId} className="sr-only">
                  {t('condition')}
                </FieldLabel>
                <MetadataConditionOperator
                  id={operatorInputId}
                  value={condition.operator}
                  onChange={(operator) =>
                    updateCondition(index, changeMetadataConditionOperator(condition, operator))
                  }
                />
              </Field>
              <MetadataConditionValue
                {...condition}
                id={valueInputId}
                onChange={(operand) => updateCondition(index, operand)}
              />
            </FieldGroup>
          </Fragment>
        );
      })}

      <Button
        size="xs"
        variant="secondary"
        className="w-fit"
        onClick={addCondition}
        disabled={conditions.length >= ALERT_RUN_CONDITIONS_MAX_COUNT}
        data-testid={`${testIdPrefix}-condition-add`}
      >
        <Plus data-icon="inline-start" />
        {t('add')}
      </Button>
    </div>
  );
};
